import * as React from "react";
import { 
    List, Show,
    Datagrid, Filter, TopToolbar, SortButton, ExportButton, SimpleShowLayout,
    TextField, NumberField, FunctionField, 
    ReferenceInput, SelectInput, RadioButtonGroupInput,
    useListSortContext, useShowController
} from 'react-admin';
import { Table, TableHead, TableBody, TableRow, TableCell, useMediaQuery, makeStyles } from '@material-ui/core';
import Config from "../../providers/configProvider";
import LogoField from '../../components/LogoField';

const ListActions = ({ sortFields }) => (
    <TopToolbar>
        <SortButton fields={sortFields} />
        <ExportButton />
    </TopToolbar>
);

const useStyles = makeStyles({
    cellSmall: {
        padding: '8px'
    },
    cell: {
        textAlign: 'right'
    },
    cellLeft: {
        textAlign: 'left'
    },
    cellBorder: {
        borderLeft: '1px solid #000',
        textAlign: 'right'
    },
    cellBorderLeft: {
        borderLeft: '1px solid #000',
        textAlign: 'left'
    },
    cellBorderDouble: {
        borderLeft: '3px double #000',
        textAlign: 'right'
    },
    topHeader: {
        fontWeight: 'bold',
        textAlign: 'center'
    },
    topHeaderBorder: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '1px solid #000'
    },
    topHeaderBorderDouble: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '3px double #000'
    },
    green: {
        fontWeight: 'bold',
        color: 'green'
    },
    greenSmall: {
        color: 'green'
    },
    red: {
        fontWeight: 'bold',
        color: 'red'
    },
    redSmall: {
        color: 'red'
    }
});

/*** TEAM STATS ***/
const teamStatsSortFields = [
    { field: 'points.for', label: 'points for' }, 
    { field: 'points.against', label: 'points against' }, 
    { field: 'points.diff', label: 'points diff' }, 
    { field: 'tries.for', label: 'tries for' }, 
    { field: 'tries.against', label: 'tries against' }, 
    { field: 'tries.diff', label: 'tries diff' }, 
    { field: 'goals.for', label: 'goals for' }, 
    { field: 'goals.against', label: 'goals against' }, 
    { field: 'goals.diff', label: 'goals diff' }, 
    { field: 'fieldGoals1.for', label: '1 point field goals for' }, 
    { field: 'fieldGoals1.against', label: '1 point field goals against' }, 
    { field: 'fieldGoals1.diff', label: '1 point field goals diff' }, 
    { field: 'fieldGoals2.for', label: '2 point field goals for' }, 
    { field: 'fieldGoals2.against', label: '2 point field goals against' }, 
    { field: 'fieldGoals2.diff', label: '2 point field goals diff' }, 
    { field: 'sinBins.for', label: 'sin bins for' }, 
    { field: 'sinBins.against', label: 'sin bins against' }, 
    { field: 'sinBins.diff', label: 'sin bins diff' }, 
    { field: 'sendOffs.for', label: 'send offs for' }, 
    { field: 'sendOffs.against', label: 'send offs against' }, 
    { field: 'sendOffs.diff', label: 'send offs diff' }
];

const TeamListRowStyle = (record, index) => ({
    backgroundColor: Config.MyTeamCodes.includes(record.teamCode) ? '#efe' : '#fff'
});

const TeamStatHeader = ({ children }) => {
    const classes = useStyles();
    const { currentSort } = useListSortContext();
    return (
        <TableHead>
            <TableRow>
                <TableCell />
                <TableCell colSpan={3} className={classes.topHeader}>Points</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Tries</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Goals</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>1 Point Field Goals</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>2 Point Field Goals</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Sin Bins</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Send Offs</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && child.props.source !== 'points.for' && child.props.source.endsWith('.for') ? classes.cellBorder : (child.props.label === 'Team' ? classes.cellLeft : classes.cell)}>
                        {child.props.label}{teamStatsSortFields.find(f => f.field === child.props.source) && currentSort.field === teamStatsSortFields.find(f => f.field === child.props.source).label
                            ? (currentSort.order === 'DESC' ? <span> &darr;</span> : <span> &uarr;</span>)
                            :  null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const TeamStatList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();

    const TeamStatFilter = props => (
        <Filter {...props}>
            <ReferenceInput label="Comp Season" source="competitionSeasonId" reference="competition-season" perPage={1000} alwaysOn allowEmpty={false}>
                <SelectInput optionText={record => `${record.name} ${record.seasonYear}`} />
            </ReferenceInput>
            <RadioButtonGroupInput label="Display" source="showAverages" choices={[
                { id: false, name: 'Totals' },
                { id: true, name: 'Averages' }
            ]} alwaysOn />
        </Filter>
    );

    const StatField = (props) => <FunctionField {...props} render={record => <NumberField {...props} options={{ minimumFractionDigits: record.showAverages ? 1 : 0 }} />} />;

    return (
        <List {...props} 
            bulkActionButtons={false} 
            filters={<TeamStatFilter />}
            filterDefaultValues={{ competitionSeasonId: Config.CurrentCompetitionSeasonId, showAverages: false }}
            actions={<ListActions sortFields={teamStatsSortFields.map(f => f.label)} />}
            sort={{ field: 'points for', order: 'DESC' }}
            pagination={null}
        >
            {isSmall ? (
                <Datagrid
                    classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }}
                    rowStyle={TeamListRowStyle}
                    rowClick={(id, basePath, record) => `/stat/teams/${record.competitionSeasonId};${record.id}/show`}
                >
                    <LogoField type="teams" source="teamCode" text="teamCode" label="Team" />
                    <StatField source="points.for" label="Pts" />                
                    <StatField source="tries.for" label="T" />
                    <StatField source="goals.for" label="G" />
                    <StatField source="fieldGoals1.for" label="FG1" />
                    <StatField source="fieldGoals2.for" label="FG2" />
                    <StatField source="sinBins.for" label="SB" />
                    <StatField source="sendOffs.for" label="SO" />
                </Datagrid>
            ) : (
                <Datagrid
                    header={<TeamStatHeader />}
                    classes={{ rowCell: classes.cell, headerCell: classes.cell }}
                    rowStyle={TeamListRowStyle}
                    rowClick={(id, basePath, record) => `/stat/teams/${record.competitionSeasonId};${record.id}/show`}
                >
                    <FunctionField render={record => record.competitionCode === 'NRL' 
                        ? <LogoField type="teams" source="teamCode" text="teamNickname" /> 
                        : <LogoField type="teams" source="teamCode" text="teamName" />
                    } label="Team" cellClassName={classes.cellLeft} />
                    <StatField source="points.for" label="F" />
                    <StatField source="points.against" label="A" />
                    <StatField source="points.diff" label="+/-" />
                    <StatField source="tries.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="tries.against" label="A" />
                    <StatField source="tries.diff" label="+/-" />
                    <StatField source="goals.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="goals.against" label="A" />
                    <StatField source="goals.diff" label="+/-" />
                    <StatField source="fieldGoals1.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="fieldGoals1.against" label="A" />
                    <StatField source="fieldGoals1.diff" label="+/-" />
                    <StatField source="fieldGoals2.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="fieldGoals2.against" label="A" />
                    <StatField source="fieldGoals2.diff" label="+/-" />
                    <StatField source="sinBins.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="sinBins.against" label="A" />
                    <StatField source="sinBins.diff" label="+/-" />
                    <StatField source="sendOffs.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="sendOffs.against" label="A" />
                    <StatField source="sendOffs.diff" label="+/-" />
                </Datagrid>
            )}
        </List>
    );
};

export const TeamStatShow = props => {
    const classes = useStyles();
    const { loading } = useShowController(props);
    if (loading) return null;

    const Title = ({ record }) => <span>Team Stats Breakdown - {record.data.competitionCode} {record.data.seasonYear} - {record.data.competitionCode === 'NRL' ? record.data.team.teamNickname : record.data.team.teamName}</span>;

    const StatRow = ({ source, label }) => (
        <TableRow>
            <TableCell className={classes.cellLeft}>{label}</TableCell>
            <TableCell className={classes.cell}><NumberField source={`${source}.for`} /></TableCell>
            <TableCell className={classes.cell}><NumberField source={`${source}.against`} /></TableCell>
            <TableCell className={classes.cell}><NumberField source={`${source}.diff`} /></TableCell>
        </TableRow>
    );

    return (
        <Show {...props} title={<Title />}>
            <SimpleShowLayout>
                <LogoField type="competitions" source="data.competitionCode" text="data.competitionCode" label="Competition" />
                <TextField source="data.seasonYear" label="Season" />
                <FunctionField render={record => record.data.competitionCode === 'NRL' 
                    ? <LogoField type="teams" source="data.team.teamCode" text="data.team.teamNickname" /> 
                    : <LogoField type="teams" source="data.team.teamCode" text="data.team.teamName" />
                } label="Team" cellClassName={classes.cellLeft} />
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.cellLeft}>Stat</TableCell>
                            <TableCell className={classes.cell}>F</TableCell>
                            <TableCell className={classes.cell}>A</TableCell>
                            <TableCell className={classes.cell}>+/-</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <StatRow source="data.team.points" label="Points" />
                        <StatRow source="data.team.tries" label="Tries" />
                        <StatRow source="data.team.goals" label="Goals" />
                        <StatRow source="data.team.fieldGoals1" label="1 Point Field Goals" />
                        <StatRow source="data.team.fieldGoals2" label="2 Point Field Goals" />
                        <StatRow source="data.team.sinBins" label="Sin Bins" />
                        <StatRow source="data.team.sendOffs" label="Send Offs" />
                    </TableBody>          
                </Table>
            </SimpleShowLayout>
        </Show>
    );
};
